import { Component } from '@theme/component';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Footer marquee. Clones the message list until the track is at least twice the strip
 * width, then animates it by -50% at a constant pixels-per-second speed.
 * Only the original list is exposed to assistive tech; clones stay clickable by pointer.
 *
 * @typedef {object} MarqueeRefs
 * @property {HTMLElement} track - The animated element.
 * @property {HTMLElement} group - The original, semantic message list.
 *
 * @extends Component<MarqueeRefs>
 */
class FooterMarquee extends Component {
  requiredRefs = ['track', 'group'];

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;
  #frame = 0;
  #lastKey = '';

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    reducedMotion.addEventListener('change', () => this.#schedule(true), { signal });

    // Theme editor: hold still while a message block is selected.
    this.addEventListener('shopify:block:select', () => this.toggleAttribute('data-paused', true), { signal });
    this.addEventListener('shopify:block:deselect', () => this.removeAttribute('data-paused'), { signal });

    // Width changes (viewport, font swap) re-measure; the strip pauses while off screen.
    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.refs.group);

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-offscreen', !entry?.isIntersecting);
    });
    this.#intersectionObserver.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#schedule(true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {boolean} [force] */
  #schedule(force = false) {
    if (force) this.#lastKey = '';
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#layout());
  }

  #layout() {
    const { track, group } = this.refs;
    const groupWidth = group.getBoundingClientRect().width;
    const containerWidth = this.clientWidth;
    const animate = !reducedMotion.matches && groupWidth > 0 && containerWidth > 0;
    const copies = animate ? Math.ceil(containerWidth / groupWidth) : 0;

    // Skip re-layout when nothing that affects the loop changed (e.g. a vertical resize).
    const key = `${copies}:${groupWidth}`;
    if (key === this.#lastKey) return;
    this.#lastKey = key;

    for (const clone of track.querySelectorAll('[data-clone]')) clone.remove();

    if (!animate) {
      this.removeAttribute('data-animate');
      return;
    }

    // Two equal halves of `copies` lists each, so translating by -50% loops seamlessly.
    const fragment = document.createDocumentFragment();
    for (let i = 1; i < copies * 2; i++) fragment.append(this.#clone(group));
    track.append(fragment);

    const speed = Number(this.dataset.speed) || 70;
    this.style.setProperty('--ft-marquee-duration', `${(groupWidth * copies) / speed}s`);
    this.toggleAttribute('data-animate', true);
  }

  /** @param {HTMLElement} group */
  #clone(group) {
    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
    clone.removeAttribute('ref');
    clone.removeAttribute('aria-label');
    clone.setAttribute('aria-hidden', 'true');
    clone.dataset.clone = '';
    for (const element of clone.querySelectorAll('[data-shopify-editor-block]')) {
      element.removeAttribute('data-shopify-editor-block');
    }
    // Clones keep their href so a shopper can click whichever copy is in front of them.
    for (const link of clone.querySelectorAll('a')) link.tabIndex = -1;
    return clone;
  }
}

/**
 * Footer signup. Posts to Relvino's app proxy with the same payload, tags and cookie as
 * Relvino's own forms. Without JavaScript the Shopify customer form posts natively.
 * Relvino needs an email for every signup, so SMS mode adds a mobile number to it.
 *
 * @typedef {object} SignupRefs
 * @property {HTMLElement} [field] - Main input and submit wrapper, hidden on success.
 * @property {HTMLInputElement} [input] - The email input, or the phone input in SMS mode.
 * @property {HTMLElement} [emailField] - Email-only row, shown in SMS mode.
 * @property {HTMLInputElement} [email] - The email input in SMS mode.
 * @property {HTMLButtonElement} [submit] - The submit button.
 * @property {HTMLElement} [status] - The single live region for errors and success.
 * @property {HTMLElement} [consent] - SMS consent copy, shown in SMS mode.
 * @property {HTMLButtonElement[]} [modes] - The Email / SMS toggle buttons.
 *
 * @extends Component<SignupRefs>
 */
class FooterSignup extends Component {
  /** @type {'email' | 'sms'} */
  #mode = 'email';
  #pending = false;

  connectedCallback() {
    super.connectedCallback();

    // Validated here so errors land in the panel, not a browser bubble.
    const form = this.querySelector('form');
    if (form && this.dataset.endpoint) form.noValidate = true;
  }

  /**
   * @param {'email' | 'sms'} mode
   */
  setMode(mode) {
    const { input, email, emailField, consent, modes = [] } = this.refs;
    const copy = this.dataset;
    if (!input || !email || !emailField || mode === this.#mode) return;

    this.#mode = mode;
    const isSms = mode === 'sms';

    for (const button of modes) {
      button.setAttribute('aria-pressed', String(button.dataset.mode === mode));
    }

    // Carry a typed email across, since SMS mode still needs it.
    if (isSms) email.value = input.value;
    else input.value = email.value;
    if (isSms) input.value = '';
    else email.value = '';

    emailField.hidden = !isSms;
    input.type = isSms ? 'tel' : 'email';
    input.name = isSms ? 'phone' : 'contact[email]';
    input.autocomplete = isSms ? 'tel-national' : 'email';
    input.inputMode = isSms ? 'tel' : 'email';
    input.placeholder = (isSms ? copy.phonePlaceholder : copy.emailPlaceholder) ?? '';
    input.setAttribute('aria-label', (isSms ? copy.phoneLabel : copy.emailLabel) ?? '');
    if (consent) consent.hidden = !isSms;
    this.#setStatus('');
  }

  /**
   * @param {SubmitEvent} event
   */
  async submit(event) {
    const copy = this.dataset;
    if (!copy.endpoint) return;

    event.preventDefault();
    const { input, field, emailField, consent, status, modes = [] } = this.refs;
    if (this.#pending || !input) return;

    const isSms = this.#mode === 'sms';
    const emailInput = isSms ? this.refs.email : input;
    const email = emailInput?.value.trim() ?? '';
    if (!emailInput || !email || !emailInput.checkValidity()) {
      this.#fail(copy.errorEmail, emailInput);
      return;
    }
    const phone = isSms ? toUsPhone(input.value) : '';
    if (isSms && !phone) {
      this.#fail(copy.errorPhone, input);
      return;
    }

    this.#setPending(true);

    /** @type {Record<string, unknown>} */
    const body = { email, tags: copy.tags, sms_opt_in: isSms, shop: window.Shopify?.shop };
    if (isSms) body.phone = phone;

    try {
      const response = await fetch(copy.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));

      // Relvino answers an existing subscriber as a success and re-sends their reward.
      if (!(response.ok && data.success) && !data.already_subscribed && data.code !== 'already_subscribed') {
        this.#setStatus(data.error || copy.errorGeneric || '');
        return;
      }

      // Stops Relvino's welcome popup asking again, as its own forms do.
      document.cookie = 'rbpopup_subscribed=true; max-age=31536000; path=/; SameSite=Lax';

      for (const element of [field, emailField, consent]) if (element) element.hidden = true;
      for (const button of modes) button.disabled = true;

      const code = data.award?.offer?.code;
      this.#setStatus((code ? copy.successCode : copy.successEmail) ?? '');
      if (code && status) {
        const codeElement = document.createElement('span');
        codeElement.className = 'ft-signup__code';
        codeElement.textContent = code;
        status.append(codeElement);
      }
      // The focused input just disappeared; keep keyboard users in the panel.
      status?.focus();
    } catch (error) {
      console.error(error);
      this.#setStatus(copy.errorGeneric ?? '');
    } finally {
      this.#setPending(false);
    }
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    const { submit } = this.refs;
    this.#pending = pending;
    this.toggleAttribute('aria-busy', pending);
    if (!submit) return;
    // aria-disabled rather than disabled, so the focused button keeps focus.
    submit.setAttribute('aria-disabled', String(pending));
    submit.textContent = (pending ? this.dataset.submittingLabel : this.dataset.submitLabel) ?? submit.textContent;
  }

  /**
   * @param {string | undefined} message
   * @param {HTMLInputElement | undefined} invalidInput
   */
  #fail(message, invalidInput) {
    this.#setStatus(message ?? '', invalidInput);
    invalidInput?.focus();
  }

  /**
   * @param {string} message
   * @param {HTMLInputElement} [invalidInput] - The input the message is about, if any.
   */
  #setStatus(message, invalidInput) {
    const { status, input, email } = this.refs;
    if (status) status.textContent = message;
    for (const element of [input, email]) {
      if (element === invalidInput) element?.setAttribute('aria-invalid', 'true');
      else element?.removeAttribute('aria-invalid');
    }
  }
}

/**
 * Normalises a US mobile number to +1XXXXXXXXXX, matching Relvino's own forms.
 * @param {string} value
 * @returns {string} The number, or an empty string when it is not 10 US digits.
 */
function toUsPhone(value) {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? `+1${digits}` : '';
}

/**
 * Footer logo. Arms a one-off "stamp" reveal that plays the first time the logo scrolls
 * into view, and pauses the breathing glow while the logo is off screen. The hidden
 * starting state is only set here, so without JavaScript, or with reduced motion, the
 * logo simply shows.
 */
class FooterLogo extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    if (reducedMotion.matches) return;

    if (!this.hasAttribute('data-revealed')) this.toggleAttribute('data-reveal', true);
    // The bottom margin keeps the reveal from playing under the mobile sticky taskbar.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.toggleAttribute('data-offscreen', !entry.isIntersecting);
        if (entry.intersectionRatio >= 0.6) this.toggleAttribute('data-revealed', true);
      },
      { threshold: [0, 0.6], rootMargin: '0px 0px -12% 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('footer-marquee')) customElements.define('footer-marquee', FooterMarquee);
if (!customElements.get('footer-signup')) customElements.define('footer-signup', FooterSignup);
if (!customElements.get('footer-logo')) customElements.define('footer-logo', FooterLogo);
